"use client";

import { cn } from "~/lib/utils";
import { Gift } from "lucide-react";
import Image from "next/image";
import { CopyToClipboardLabel } from "~/hooks/use-copy-to-clipboard";

interface GiftCardPreviewProps {
  amount: number;
  recipientName?: string;
  senderName?: string;
  message?: string;
  giftCode?: string;
  className?: string;
}

export function GiftCardPreview({
  amount,
  recipientName,
  senderName,
  message,
  giftCode,
  className,
}: GiftCardPreviewProps) {
  const displayCode = giftCode?.trim();

  return (
    <div className={cn("perspective-1000 mx-auto w-full max-w-md", className)}>
      <div
        className={cn(
          "hover:shadow-brand/20 relative aspect-[1.586/1] w-full overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 hover:scale-[1.02]",
          "bg-brand-forest text-white",
          "border border-white/10",
        )}
      >
        <Image
          src="/images/brand/gift-voucher-forest-botanical.webp"
          alt=""
          fill
          loading="eager"
          sizes="(max-width: 480px) calc(100vw - 32px), 448px"
          className="pointer-events-none object-cover"
        />
        <div
          className="bg-brand-forest/30 absolute inset-0"
          aria-hidden="true"
        />

        <div className="relative flex h-full flex-col justify-between p-4 sm:p-8">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide sm:text-sm">
                <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-decorative shadow-[0_0_8px_rgba(13,148,136,0.6)]" />
                <span>Poklon Vaučer</span>
              </div>
              <div className="text-[10px] font-medium tracking-wider text-white/70 sm:text-xs">
                REŠIMO PROBLEME
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold tabular-nums tracking-tight text-white sm:text-4xl">
                €{amount}
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center py-2 sm:py-4">
            {message ? (
              <p className="line-clamp-2 shrink-0 font-sans text-sm leading-relaxed text-white/90 italic sm:line-clamp-3 sm:text-base">
                &ldquo;{message}&rdquo;
              </p>
            ) : null}
          </div>

          <div className="space-y-2 sm:space-y-4">
            {(recipientName || senderName) && (
              <div className="grid grid-cols-2 gap-4">
                <div className={recipientName ? undefined : "invisible"}>
                  <p className="mb-1 text-[10px] font-medium tracking-wider text-white/70 uppercase sm:text-xs">
                    Za
                  </p>
                  <p className="truncate text-sm font-medium text-white sm:text-base">
                    {recipientName}
                  </p>
                </div>
                <div className={cn("text-right", !senderName && "invisible")}>
                  <p className="mb-1 text-[10px] font-medium tracking-wider text-white/70 uppercase sm:text-xs">
                    Od
                  </p>
                  <p className="truncate text-sm font-medium text-white sm:text-base">
                    {senderName}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-white/10 pt-2 sm:pt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur-xs">
                  <Gift className="h-4 w-4 text-white/80" />
                </div>
                <div className="text-[10px] font-medium uppercase tracking-wider text-white/60">
                  Poklon kod
                </div>
              </div>

              {displayCode ? (
                <CopyToClipboardLabel
                  text={displayCode}
                  className="space-x-2 font-mono text-sm tracking-[0.2em] text-white/90"
                >
                  <span>{displayCode}</span>
                </CopyToClipboardLabel>
              ) : (
                <span className="text-xs text-white/80">
                  Kod stiže posle kupovine
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-4 left-4 right-4 h-4 rounded-[100%] bg-black/20 blur-xl" />
    </div>
  );
}
